/**
 * useLiveChats.js
 *
 * Hook for managing live chat conversations.
 * Used by Admin and Client Success dashboards.
 */
import { useState, useEffect } from "react";
import { db, getFunctionsClient } from "../firebaseConfig";
import { havloDb } from "../havloFirebaseConfig";
import {
  collection,
  onSnapshot,
  doc,
  updateDoc,
  addDoc,
  arrayUnion,
  serverTimestamp,
  getDoc,
} from "firebase/firestore";
import { httpsCallable } from "firebase/functions";

// Havlo workspace ID for TheScope180
const HAVLO_WORKSPACE_ID = "Q6qETEYaE9SxhkqTGos7o1f5hqk2";
export const havloConversationsPath = `workspaces/${HAVLO_WORKSPACE_ID}/conversations`;

const OPEN_STATUSES = new Set(["open", "queued", "active", "waiting"]);

// Havlo's bot sends one of these when a visitor asks for a person and no agent
// is online. Havlo has no needsHumanAttention flag, so this is the signal.
export const HAVLO_HANDOFF_RE =
  /no live agents|connect you with a team member|leave your message|\b(human|real person|live (rep|agent|person)|representative|speak to (someone|a person)|talk to (someone|a person))\b/i;

// Havlo stores timestamps as either Firestore Timestamps or ISO strings.
export function toIso(value) {
  if (!value) return null;
  if (typeof value.toDate === "function") return value.toDate().toISOString();
  const d = new Date(value);
  return Number.isNaN(d.getTime()) ? null : d.toISOString();
}

// Havlo message `type` -> the `sender` values the dashboard renders
const HAVLO_SENDER = { client: "visitor", bot: "ai", agent: "rep", system: "system" };

function normalizeHavloMessage(id, data) {
  return {
    id,
    ...data,
    sender: data.sender || HAVLO_SENDER[data.type] || "visitor",
    timestamp: toIso(data.createdAt),
  };
}

// Map a Havlo conversation doc to the shape LiveSupportDashboard expects
export function normalizeHavloConversation(id, data) {
  return {
    id,
    ...data,
    visitorName: data.visitorName || data.visitorEmail || data.visitorId || "Visitor",
    status: data.status === "open" ? "active" : data.status === "queued" ? "waiting" : data.status,
    isOpen: OPEN_STATUSES.has(data.status),
    humanTakeover: !!data.assignedAgentId,
    needsHumanAttention:
      OPEN_STATUSES.has(data.status) && !data.assignedAgentId && HAVLO_HANDOFF_RE.test(data.lastMessage || ""),
    lastMessageText: data.lastMessage || "",
    lastMessageIso: toIso(data.lastMessageAt) || toIso(data.updatedAt) || toIso(data.createdAt),
  };
}

/**
 * Hook to get Havlo live chats, split into open and closed/archived
 * @returns {{ chats: Array, closedChats: Array, loading: boolean, activeCount: number, error: string|null }}
 */
export function useLiveChats() {
  const [chats, setChats] = useState([]);
  const [closedChats, setClosedChats] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    let isCurrent = true;
    let unsubscribe = null;

    // Timeout fallback to prevent infinite loading
    const loadingTimeout = setTimeout(() => {
      if (isCurrent && loading) {
        setLoading(false);
      }
    }, 3000);

    try {
      // Use simpler query without composite index requirement
      // Filter client-side instead to avoid index issues
      // Read from HAVLO Firestore — conversations for our workspace
      // Sorted client-side: a query orderBy would silently drop conversations
      // missing lastMessageAt.
      const q = collection(havloDb, havloConversationsPath);

      unsubscribe = onSnapshot(
        q,
        (snapshot) => {
          if (!isCurrent) return;
          const all = snapshot.docs
            .map((d) => normalizeHavloConversation(d.id, d.data()))
            .sort((a, b) => (b.lastMessageIso || "").localeCompare(a.lastMessageIso || ""));
          setChats(all.filter((c) => c.isOpen));
          setClosedChats(all.filter((c) => !c.isOpen));
          setLoading(false);
          setError(null);
        },
        (err) => {
          if (!isCurrent) return;
          // Index not ready or collection doesn't exist - not a critical error
          if (err.code === "failed-precondition" || err.code === "unavailable") {
            console.warn("Live chats: index not ready or empty collection. Check console for index creation link.");
            setChats([]);
          } else {
            console.error("Live chats error:", err);
            setError(err.message);
          }
          setLoading(false);
        }
      );
    } catch (err) {
      console.error("Failed to setup live chats listener:", err);
      setLoading(false);
      setError(err.message);
    }

    return () => {
      isCurrent = false;
      clearTimeout(loadingTimeout);
      if (unsubscribe) {
        try {
          unsubscribe();
        } catch (e) {
          // Ignore cleanup errors
        }
      }
    };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return {
    chats,
    closedChats,
    loading,
    activeCount: chats.length,
    error,
  };
}

/**
 * Hook to get a single chat conversation with real-time updates
 * @param {string} chatId
 * @returns {{ chat: Object, messages: Array, loading: boolean }}
 */
export function useChatConversation(chatId) {
  const [chat, setChat] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!chatId) {
      setLoading(false);
      setChat(null);
      return;
    }

    let isCurrent = true;
    let unsubscribe = null;

    try {
      // Listen to Havlo conversation doc
      const convoUnsub = onSnapshot(
        doc(havloDb, havloConversationsPath, chatId),
        (docSnap) => {
          if (!isCurrent) return;
          if (docSnap.exists()) {
            const convo = normalizeHavloConversation(docSnap.id, docSnap.data());
            setChat((prev) => ({ ...convo, messages: prev?.id === convo.id ? prev.messages : [] }));
          } else {
            setChat(null);
          }
          setLoading(false);
        },
        (error) => {
          if (!isCurrent) return;
          console.error("Chat conversation error:", error);
          setLoading(false);
        }
      );

      // Also listen to Havlo messages subcollection
      const msgsUnsub = onSnapshot(
        // No orderBy: Havlo mixes ISO-string and Timestamp createdAt values, and
        // Firestore sorts those by type first, which scrambles the order.
        collection(havloDb, havloConversationsPath, chatId, "messages"),
        (snap) => {
          if (!isCurrent) return;
          const msgs = snap.docs
            .map((d) => normalizeHavloMessage(d.id, d.data()))
            .sort((a, b) => (a.timestamp || "").localeCompare(b.timestamp || ""));
          setChat((prev) => ({ ...(prev || { id: chatId }), messages: msgs }));
        },
        (err) => console.error("Chat messages error:", err)
      );

      unsubscribe = () => { convoUnsub(); msgsUnsub(); };
    } catch (err) {
      console.error("Failed to setup chat listener:", err);
      setLoading(false);
    }

    return () => {
      isCurrent = false;
      if (unsubscribe) {
        try {
          unsubscribe();
        } catch (e) {
          // Ignore
        }
      }
    };
  }, [chatId]);

  return {
    chat,
    messages: chat?.messages || [],
    loading,
  };
}

/**
 * Take over a chat from AI
 * @param {string} chatId
 * @param {string} repId - User ID of the rep taking over
 * @param {string} repDisplayName - Display name like "Sarah from Client Success"
 */
export async function takeOverChat(chatId, repId, repDisplayName) {
  try {
    // Update Havlo conversation
    await updateDoc(doc(havloDb, havloConversationsPath, chatId), {
      assignedAgentId: repId,
      assignedAgentName: repDisplayName,
      status: "open",
      lastMessageAt: serverTimestamp(),
    });
    // Add system message to Havlo messages subcollection
    await addDoc(collection(havloDb, havloConversationsPath, chatId, "messages"), {
      text: `${repDisplayName} has joined the conversation.`,
      type: "system",
      senderId: "system",
      createdAt: serverTimestamp(),
    });
    return { success: true };
  } catch (error) {
    console.error("Takeover error:", error);
    return { success: false, error: error.message };
  }
}

/**
 * Send a message as a support rep
 * @param {string} chatId
 * @param {string} text
 * @param {string} senderId
 * @param {string} senderName
 */
export async function sendRepMessage(chatId, text, senderId, senderName) {
  try {
    // Add message to Havlo messages subcollection
    await addDoc(collection(havloDb, havloConversationsPath, chatId, "messages"), {
      text,
      type: "agent",
      senderId,
      senderName,
      createdAt: serverTimestamp(),
    });
    // Update conversation lastMessageAt
    await updateDoc(doc(havloDb, havloConversationsPath, chatId), {
      lastMessageAt: serverTimestamp(),
    });
    return { success: true };
  } catch (error) {
    console.error("Send message error:", error);
    return { success: false, error: error.message };
  }
}

/**
 * Get AI-suggested replies for a conversation
 * @param {Array} messages
 * @returns {Promise<Array<string>>}
 */
export async function getSuggestedReplies(messages) {
  try {
    const chatSupportAI = httpsCallable(getFunctionsClient(), "chatSupportAI");
    const result = await chatSupportAI({
      action: "suggestReplies",
      messages,
    });
    return result.data?.suggestions || [];
  } catch (error) {
    console.error("Suggested replies error:", error);
    return [
      "How can I help you further?",
      "Would you like more information about our courses?",
      "I'd be happy to assist with that!",
    ];
  }
}

/**
 * Close/resolve a chat
 * @param {string} chatId
 */
export async function closeChat(chatId) {
  try {
    await updateDoc(doc(havloDb, havloConversationsPath, chatId), {
      status: "closed",
      closedAt: serverTimestamp(),
    });
    await addDoc(collection(havloDb, havloConversationsPath, chatId, "messages"), {
      text: "This conversation has been closed. Thank you for chatting with us!",
      type: "system",
      senderId: "system",
      createdAt: serverTimestamp(),
    });
    return { success: true };
  } catch (error) {
    console.error("Close chat error:", error);
    return { success: false, error: error.message };
  }
}

/**
 * Create an outbound chat initiated by a support rep
 * @param {{ repId: string, repName: string, userId: string, userName: string, userEmail: string, initialMessage: string }}
 * @returns {Promise<string>} The new chat document ID
 */
export async function createOutboundChat({ repId, repName, userId, userName, userEmail, initialMessage }) {
  const docRef = await addDoc(collection(db, "liveChats"), {
    visitorId: `outbound_${userId}`,
    visitorName: userName,
    userId,
    userEmail,
    status: "active",
    origin: "outbound",
    humanTakeover: true,
    assignedTo: repId,
    assignedName: repName,
    aiMessageCount: 0,
    messages: [{
      id: `rep-${Date.now()}`,
      text: initialMessage,
      sender: "rep",
      senderId: repId,
      senderName: repName,
      timestamp: new Date().toISOString(),
    }],
    startedAt: serverTimestamp(),
    lastMessageAt: serverTimestamp(),
  });
  return docRef.id;
}

export default useLiveChats;
